/**
 * Immutable helpers for updating backend-provided notification read state in
 * the date-scoped worklist cache.
 */

import type { DocumentType, Patient, UnreadDocumentCounts } from "../types";

const LEGACY_READ_STATE_KEY = "clinical-trials:read-notifications:v1";
let legacyStateCleared = false;

/**
 * Remove the obsolete browser-local read-state payload once per app session.
 *
 * @param storage - Browser storage containing the temporary legacy key
 * @returns Nothing
 */
export function clearLegacyNotificationReadState(
  storage: Storage = window.localStorage,
): void {
  if (legacyStateCleared) return;
  try {
    storage.removeItem(LEGACY_READ_STATE_KEY);
  } catch {
    // Backend state remains authoritative when browser storage is unavailable.
  }
  legacyStateCleared = true;
}

/**
 * Adjust one category's unread count without mutating cached state.
 *
 * @param counts - Current personalized unread report counts
 * @param documentType - Frontend document category to update
 * @param delta - Signed count adjustment, usually -1 or +1
 * @returns Copied counts with the category clamped to zero
 */
export function adjustUnreadDocumentCount(
  counts: UnreadDocumentCounts,
  documentType: DocumentType,
  delta: number,
): UnreadDocumentCounts {
  return {
    ...counts,
    [documentType]: Math.max(0, (counts[documentType] ?? 0) + delta),
  };
}

/**
 * Copy a worklist while updating one durable document's read flag.
 *
 * @param source - Base patient rows cached for the selected date
 * @param mrn - Patient medical record number
 * @param backendDocumentType - Canonical backend document category
 * @param documentId - Durable backend document id
 * @param isRead - Replacement per-user read state
 * @returns Copied patient rows with the matching document updated
 */
export function updateNotificationDocumentReadState(
  source: Patient[],
  mrn: string,
  backendDocumentType: string,
  documentId: string,
  isRead: boolean,
): Patient[] {
  return source.map((patient) => {
    if (patient.mrn !== mrn) return patient;
    const documents = patient.latestDocsByType?.[backendDocumentType];
    if (!documents) return patient;
    return {
      ...patient,
      latestDocsByType: {
        ...patient.latestDocsByType,
        [backendDocumentType]: documents.map((document) =>
          document.documentId === documentId
            ? { ...document, isRead }
            : document,
        ),
      },
    };
  });
}
